import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "../components/Logo";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { updatePassword, signOut } from "../lib/authApi";

const fieldClass =
  "border-white/10 bg-[#121212] text-white placeholder:text-white/30 focus-visible:ring-[#ff2a00]";

export default function ResetPassword() {
  const navigate = useNavigate();
  const { recoveryMode, setRecoveryMode, user, loading } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // A recovery link establishes a session and lands here. Show the set-password
  // form whenever we're in recovery mode OR a session is present on this page —
  // this prevents the user being silently "logged in" if the PASSWORD_RECOVERY
  // event fired before the auth listener subscribed.
  const showForm = recoveryMode || !!user;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await updatePassword(password);
      setRecoveryMode(false);
      // End the temporary recovery session so the user logs in fresh.
      await signOut();
      toast.success("Password updated. Please log in.");
      navigate("/login");
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="reset-page" className="min-h-[100dvh] bg-[#050505] px-6 pb-16 pt-16">
      <div className="mx-auto max-w-md">
        <Logo />
        <div className="mt-8 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#ff2a00]/15">
          <KeyRound className="h-7 w-7 text-[#ff2a00]" />
        </div>
        <h1 className="font-display mt-5 text-3xl font-extrabold tracking-tight text-white">
          Set a new password
        </h1>

        {!showForm ? (
          <p className="mt-3 text-[15px] leading-relaxed text-white/60">
            {loading
              ? "Loading…"
              : "Open the password-reset link from your email to continue. If you got here by mistake, you can "}
            {!loading && (
              <button
                onClick={() => navigate("/login")}
                className="font-semibold text-[#ff2a00]"
              >
                go back to log in
              </button>
            )}
            {!loading && "."}
          </p>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-white/50">
                New password
              </Label>
              <Input
                data-testid="reset-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 6 characters"
                className={fieldClass}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold uppercase tracking-wide text-white/50">
                Confirm new password
              </Label>
              <Input
                data-testid="reset-confirm"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Re-enter password"
                className={fieldClass}
              />
            </div>
            {error && <p className="text-sm text-[#ff6a4d]">{error}</p>}
            <button
              type="submit"
              data-testid="reset-submit"
              disabled={busy}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-[#ff2a00] py-3.5 text-base font-bold text-white transition-transform duration-200 active:scale-[0.98] disabled:opacity-60"
            >
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Update password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
